// tempurpedic UtilityPage left side-nav, rendered in-tree. The page.nav (NavBar
// snippet) body is rendered TWICE -- a desktop `accordion__list-item--desktop`
// and a mobile `accordion__list-item--mobile` (with a MENU toggle keyed by the
// navbar name). Only `link` / `link_list` block types render (the chrome nav
// builder filters to those two). hrefs + active state are resolved SERVER-SIDE
// (a PageChooserBlock serializes to an id, not a url).
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

type INavItem = templates.UtilityPageTemplate["nav_items"][number];

// Reproduce Django's `slugify` for the link-list-block accordion id.
const slugify = (value: string): string =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_]+/g, "-")
        .replace(/^-+|-+$/g, "");

const NavLink = ({ item }: { item: INavItem }): React.JSX.Element => (
    <div className="accordion__list-item">
        <a
            href={item.href}
            className={item.is_active ? "accordion--is-active" : undefined}
        >
            {item.display_name}
        </a>
    </div>
);

// Each list accordion owns its open/close state. The desktop side-nav renders
// expanded (the legacy utility-page on-load pass added the active classes to
// every `.accordion__list-item--desktop` accordion); the mobile menu renders
// collapsed.
const NavList = ({
    item,
    initialExpanded,
}: {
    item: INavItem;
    initialExpanded: boolean;
}): React.JSX.Element => {
    const id = slugify(item.list_header);
    const [isExpanded, setIsExpanded] = React.useState(initialExpanded);
    return (
        <div className="accordion__list-item">
            <a
                href="#"
                title={t`Toggle Answer`}
                className={
                    isExpanded ? "accordion accordion--is-active" : "accordion"
                }
                aria-controls={id}
                aria-expanded={isExpanded ? "true" : "false"}
                onClick={(event) => {
                    event.preventDefault();
                    setIsExpanded((value) => !value);
                }}
            >
                {item.list_header} <span></span>
            </a>
            <div
                className={
                    isExpanded
                        ? "accordion-target accordion-target--is-active"
                        : "accordion-target"
                }
                id={id}
                aria-hidden={isExpanded ? "false" : "true"}
            >
                <ul className="accordion-target__list">
                    {item.links.map((link, i) => (
                        <li key={i} className="accordion-target__list-item">
                            <a
                                href={link.href}
                                className={
                                    link.is_active ? "is-active" : undefined
                                }
                            >
                                {link.display_name}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

const NavItems = ({
    items,
    initialExpanded,
}: {
    items: readonly INavItem[];
    initialExpanded: boolean;
}): React.JSX.Element => (
    <>
        {items.map((item, i) =>
            item.kind === "link" ? (
                <NavLink key={i} item={item} />
            ) : (
                <NavList
                    key={i}
                    item={item}
                    initialExpanded={initialExpanded}
                />
            ),
        )}
    </>
);

type IProps = {
    items: readonly INavItem[];
    navName: string;
};

export const UtilitySideNav = (props: IProps): React.JSX.Element => {
    const { items, navName } = props;
    const [mobileMenuExpanded, setMobileMenuExpanded] = React.useState(false);
    return (
        <>
            <div
                className="accordion__list-item accordion__list-item--desktop"
                role="region"
                aria-label={t`Services menu`}
            >
                <NavItems items={items} initialExpanded={true} />
            </div>
            <div
                className="accordion__list-item accordion__list-item--mobile"
                role="region"
                aria-label={t`Services menu`}
            >
                <a
                    href="#"
                    title={t`Toggle Menu`}
                    className={
                        mobileMenuExpanded
                            ? "category-list-side-nav__mobile-toggle accordion accordion--is-active"
                            : "category-list-side-nav__mobile-toggle accordion"
                    }
                    aria-expanded={mobileMenuExpanded ? "true" : "false"}
                    aria-controls={navName}
                    onClick={(event) => {
                        event.preventDefault();
                        setMobileMenuExpanded((value) => !value);
                    }}
                >
                    {t`menu`}
                </a>
                <div
                    id={navName}
                    className={
                        mobileMenuExpanded
                            ? "accordion-target accordion-target--is-active"
                            : "accordion-target"
                    }
                    aria-hidden={mobileMenuExpanded ? "false" : "true"}
                >
                    <NavItems items={items} initialExpanded={false} />
                </div>
            </div>
        </>
    );
};
